import { useEffect, useId, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api, type LocationOption } from "@/services/api";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { cn } from "@/utils/cn";

export interface LocationSelectProps {
  name?: string;
  id?: string;
  /** The saved location string, e.g. "Houston, TX, USA" (or any free text). */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
}

type Row = { key: string; label: string; value: string; hint?: string };

const KIND_HINT: Record<LocationOption["kind"], string | undefined> = {
  COUNTRY: "Country",
  STATE: "State / Region",
  CITY: undefined,
  SPECIAL: undefined,
};

/**
 * Searchable State / Region field.
 *
 * Type-ahead over ~37k countries, states and cities served by
 * GET /master-data/locations/search, so users find "Florida" or "Houston, TX"
 * instead of scrolling a fixed list. When nothing fits, the last row offers the
 * typed text itself, so no one is blocked by a missing place.
 *
 * The input only commits on a pick: blurring or Escape with half-typed text
 * puts the saved value back.
 */
export const LocationSelect = ({
  name,
  id,
  value,
  onChange,
  placeholder = "Search city, state or country",
  className,
  error,
}: LocationSelectProps) => {
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value);
  const [active, setActive] = useState(0);
  const [query, resetQuery] = useDebouncedValue(text.trim(), 250);

  // Show the saved value whenever the user isn't mid-search.
  useEffect(() => {
    if (!open) setText(value);
  }, [value, open]);

  const searching = open && query.length > 0 && query !== value;
  const { data: results = [], isFetching } = useQuery({
    queryKey: ["location-search", query],
    queryFn: () => api.master.searchLocations(query),
    enabled: searching,
    staleTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const rows: Row[] = useMemo(() => {
    if (!searching) return [];
    const found = results.map((r) => ({
      key: r.name,
      label: r.name,
      value: r.name,
      hint: KIND_HINT[r.kind],
    }));
    const typed = text.trim();
    const exact = results.some((r) => r.name.toLowerCase() === typed.toLowerCase());
    if (typed && !exact) {
      found.push({ key: "__free_text__", label: `Use "${typed}"`, value: typed, hint: undefined });
    }
    return found;
  }, [searching, results, text]);

  useEffect(() => setActive(0), [rows.length, query]);

  const close = () => {
    setOpen(false);
    setText(value);
    resetQuery(value);
  };

  const pick = (row: Row) => {
    onChange(row.value);
    setOpen(false);
    setText(row.value);
    resetQuery(row.value);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(rows.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (open && rows[active]) {
        e.preventDefault(); // don't submit the form mid-pick
        pick(rows[active]);
      }
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      close();
    }
  };

  const showPanel = open && (rows.length > 0 || query.length === 0 || isFetching);

  return (
    <div className={cn("relative w-full min-w-0", className)}>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={showPanel && rows[active] ? `${listboxId}-${active}` : undefined}
        autoComplete="off"
        className={cn(
          "input form-style-light",
          value && "pr-9",
          error && "border-error-500 focus:border-error-500 focus:ring-error-500",
        )}
        style={{ fontSize: "16px" }}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={(e) => {
          setOpen(true);
          e.target.select(); // typing replaces the saved value
        }}
        onBlur={close}
        onKeyDown={onKeyDown}
      />

      {value && (
        <button
          type="button"
          aria-label="Clear location"
          // mousedown, not click: keeps focus in the input so blur doesn't fire first.
          onMouseDown={(e) => {
            e.preventDefault();
            onChange("");
            setText("");
            resetQuery("");
            inputRef.current?.focus();
          }}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-theme-text-secondary hover:text-theme-text-heading"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {showPanel && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-[70] mt-1 max-h-[320px] overflow-y-auto rounded-md border border-theme-border bg-theme-bg-surface py-1 text-theme-text shadow-md"
        >
          {query.length === 0 ? (
            <li className="px-3 py-2 text-13 text-theme-text-secondary">
              Type to search cities, states or countries
            </li>
          ) : rows.length === 0 ? (
            <li className="px-3 py-2 text-13 text-theme-text-secondary">Searching…</li>
          ) : (
            rows.map((row, i) => (
              <li
                key={row.key}
                id={`${listboxId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(row);
                }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  "flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-14",
                  i === active && "bg-theme-accent-subtle text-theme-accent",
                  row.key === "__free_text__" && "italic",
                )}
              >
                <span className="truncate">{row.label}</span>
                {row.hint && (
                  <span className="shrink-0 text-12 text-theme-text-secondary">{row.hint}</span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
